/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * Root Reducer
 * combine all reducers to create root reducer
 */

import { combineReducers, type Action } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import Storage from 'redux-persist/lib/storage';

import { bootstrapReducer } from '@app/modules/bootstrap';
import { authReducer, logout } from '@app/modules/auth';
import { commonReducer } from '@app/modules/common';
import { homeReducer } from '@app/modules/home';
import { favoriteReducer } from '@app/modules/favorites';
import { selectIngredientsScreenReducer } from '@app/modules/select-ingredients';
import { searchScreenReducer } from '@app/modules/search';
import { myMealsReducer } from '@app/modules/my-meals';
import { manageProfileSagasReducer } from '@app/modules/manage-profile';

/* -----[ persist configurations ]------ */
const rootPersistConfig = {
  key: 'root',
  storage: Storage,
  whitelist: [
    'version',
    'commonReducer',
    'authReducer',
    'profileSlice',
    'myMealsReducer',
    'searchScreenReducer',
    'favoriteReducer',
    'selectIngredientsScreenReducer',
    'manageProfileSagasReducer',
  ],
};

// Preserver below slice even after logout
const preserveAlways = ['bootstrapReducer'];
const stateVersion = 1; // Update it whenever change state structure of whitelist slice

const appReducer = combineReducers({
  bootstrapReducer,
  authReducer,
  commonReducer,
  homeReducer,
  favoriteReducer,
  selectIngredientsScreenReducer,
  searchScreenReducer,
  myMealsReducer,
  manageProfileSagasReducer,
  version: (state = stateVersion) => state,
});

const rootReducer = (state: ReturnType<typeof appReducer>, action: Action<any>) => {
  if (action.type === logout.type) {
    const newState: any = Object.keys(state).reduce((acc, key) => {
      if (preserveAlways.includes(key)) {
        (acc as any)[key] = (state as any)[key];
      } else {
        (acc as any)[key] = undefined;
      }
      return acc;
    }, {});
    return appReducer(newState, action);
  }

  return appReducer(state, action);
};

const persistRootReducer = persistReducer(rootPersistConfig, (state, action) => {
  const migratedState = migrateState(state);
  return rootReducer(migratedState, action);
});

const migrateState = (state: any) => {
  if (state !== undefined && state.version !== stateVersion) {
    return {
      ...state,
      version: stateVersion,
    };
  }

  return state;
};

export type RootState = ReturnType<typeof rootReducer>;

export { persistRootReducer };
